import React from 'react';

import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis } from 'recharts';

import SidePanel from 'src/components/sidePanel/SidePanel';
import { cardMessages } from 'src/features/cards/utils/messages';
import { useListPaymentDetailsStatistics } from 'src/features/statistics/api/useListPaymentDetailsStatistics';
import { useListPaymentsByItemStatistics } from 'src/features/statistics/api/useListPaymentsByItemStatistics';
import { useListPaymentsByTypeStatistics } from 'src/features/statistics/api/useListPaymentsByTypeStatistics';
import { useListPaymentTypes } from 'src/features/statistics/api/useListPaymentTypes';
import { useListTotalPaymentsStatistics } from 'src/features/statistics/api/useListTotalPaymentsStatistics';
import { useListUsers } from 'src/features/statistics/api/useListUsers';
import { useListWorkingPlaces } from 'src/features/statistics/api/useListWorkingPlaces';
import { CustomDataTableControls } from 'src/features/statistics/components/CustomDataTableControls';
import PaymentDetails from 'src/features/statistics/components/PaymentDetails';
import PaymentExport from 'src/features/statistics/components/PaymentExport';
import StatisticsCards from 'src/features/statistics/components/StatisticsCards';
import { StatisticsOverviewTooltip } from 'src/features/statistics/components/StatisticsOverviewTooltip';
import {
    PAYMENT_EXPORT_PROPS,
    PAYMENT_TYPE,
    PAYMENT_TYPE_MAP,
    type StatisticPaymentDetails,
} from 'src/features/statistics/types';
import { statisticsMessages, TOTAL_SPACES_NAME } from 'src/features/statistics/utils';
import useFilters from 'src/hooks/useFilters';
import usePagination from 'src/hooks/usePagination';
import useSearch from 'src/hooks/useSearch';
import useSorting from 'src/hooks/useSorting';
import { useParking } from 'src/providers/ParkingProvider';
import { GeneralBlock, GeneralBlockHeader, GeneralBlockTitle } from 'src/registry/components/blocks/GeneralBlock';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import {
    SectionBlock,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSeparator,
    SectionBlockSubtitle,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import {
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTablePagination,
    tableMessages,
} from 'src/registry/components/table';
import { CellPrice } from 'src/registry/components/table/cells/CellPrice';
import DataTableDateDropdown from 'src/registry/components/table/DataTableDateDropdown';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useRowSelection from 'src/registry/hooks/useRowSelection';
import { Button } from 'src/registry/ui/Button';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { useTitleStore } from 'src/stores/useTitleStore';

export default function Payments() {
    const { parkingId } = useParking();
    const PAYMENTS_PANEL_NAME = 'payments-panel';

    const { setTitle, removeTitle } = useTitleStore();
    const [dateTime, setDateTime] = React.useState(() => {
        const currentDate = new Date();
        const today = new Date(currentDate.getTime());
        today.setHours(23);
        today.setMinutes(59);
        today.setSeconds(59);
        const lastWeek = new Date(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7).getTime());
        return { afterDate: lastWeek, beforeDate: today };
    });

    React.useEffect(() => {
        setTitle(formatMessage(statisticsMessages.payments));
        return removeTitle;
    }, []);

    const { formatMessage, formatNumber } = useIntl();

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'payment_datetime', desc: true }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'card_type' });
    const { clearRowSelection, ...rowSelection } = useRowSelection({ sidePanelName: PAYMENTS_PANEL_NAME });

    const columnHelper = createColumnHelper<StatisticPaymentDetails>();

    const { data: workingPlacesData } = useListWorkingPlaces({ parkingId });
    const { data: paymentTypes } = useListPaymentTypes({ parkingId });
    const { data: users } = useListUsers({ parkingId });

    const { data: paymentsByType, isLoading: isLoadingByType } = useListPaymentsByTypeStatistics({
        parkingId,
        to_date: dateTime.beforeDate.toISOString(),
        from_date: dateTime.afterDate.toDateString(),
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
    });

    const { data: paymentsByItem, isLoading: isLoadingByItem } = useListPaymentsByItemStatistics({
        parkingId,
        to_date: dateTime.beforeDate.toISOString(),
        from_date: dateTime.afterDate.toISOString(),
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
    });

    const { data: totalPayments } = useListTotalPaymentsStatistics({
        parkingId,
        to_date: dateTime.beforeDate.toISOString(),
        from_date: dateTime.afterDate.toISOString(),
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
    });

    const {
        data: paymentDetails,
        isLoading,
        isError,
    } = useListPaymentDetailsStatistics({
        parkingId,
        to_date: dateTime.beforeDate.toISOString(),
        from_date: dateTime.afterDate.toISOString(),
        ...querySortingOptions,
        ...queryPaginationOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
    });

    const DataTableControls = React.useMemo(
        () =>
            CustomDataTableControls(
                <>
                    <DataTableDateDropdown
                        setValue={setDateTime}
                        inline
                        afterDate={dateTime.afterDate}
                        beforeDate={dateTime.beforeDate}
                    />
                    <PaymentExport
                        disabled={paymentDetails?.count < 1}
                        parkingId={parkingId}
                        dateTime={dateTime}
                        querySortingOptions={querySortingOptions}
                        queryFiltersOptions={queryFiltersOptions}
                    />
                </>,
            ),
        [paymentDetails, setDateTime, dateTime, parkingId, querySortingOptions, queryFiltersOptions],
    );

    const columns = [
        columnHelper.accessor(PAYMENT_EXPORT_PROPS.IDENTIFIER, {
            header: DataTableColumnHeader,
            meta: {
                title: formatMessage(cardMessages.cardNumber),
            },
        }),
        columnHelper.accessor(PAYMENT_EXPORT_PROPS.PAYMENT_DATETIME, {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(statisticsMessages.date),
            },
        }),
        columnHelper.accessor(PAYMENT_EXPORT_PROPS.ITEM_NAME, {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(statisticsMessages.category),
            },
        }),
        columnHelper.accessor(PAYMENT_EXPORT_PROPS.PAYMENT_TYPE_NAME, {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (cell) => {
                const value = cell.getValue() as PAYMENT_TYPE;
                const message = PAYMENT_TYPE_MAP[value];

                return message ? formatMessage(message) : value;
            },
            enableColumnFilter: true,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(cardMessages.paymentMethod),
                options: paymentTypes?.results.map((type) => {
                    const message = PAYMENT_TYPE_MAP[type.name];

                    return {
                        value: type.id,
                        label: message ? formatMessage(message) : type.name,
                    };
                }),
            },
            id: 'payment_type_code',
        }),
        columnHelper.accessor(PAYMENT_EXPORT_PROPS.ITEM_QUANTITY, {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(statisticsMessages.quantity),
            },
        }),
        columnHelper.accessor(PAYMENT_EXPORT_PROPS.PRICE_NO_VAT, {
            header: DataTableColumnHeader,
            cell: CellPrice,
            enableHiding: true,
            meta: {
                title: formatMessage(statisticsMessages.withoutVat),
            },
        }),
        columnHelper.accessor(PAYMENT_EXPORT_PROPS.PRICE_VAT_ONLY, {
            header: DataTableColumnHeader,
            cell: CellPrice,
            enableHiding: true,
            meta: {
                title: formatMessage(cardMessages.vatAmount),
            },
        }),
        columnHelper.accessor(PAYMENT_EXPORT_PROPS.PRICE_TOTAL, {
            header: DataTableColumnHeader,
            cell: CellPrice,
            enableHiding: true,
            meta: {
                title: formatMessage(statisticsMessages.withVat),
            },
        }),
        columnHelper.accessor(PAYMENT_EXPORT_PROPS.FISCAL_NOTE_NUMBER, {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(statisticsMessages.fiscalNoteNumber),
            },
        }),
        columnHelper.accessor(PAYMENT_EXPORT_PROPS.OPERATOR_NAME, {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(cardMessages.cardTypeOperator),
                options: users?.results.map((type) => ({ value: type.id, label: type.name })),
            },
            id: 'operator_id',
        }),
        columnHelper.accessor(PAYMENT_EXPORT_PROPS.WORKING_PLACE_NAME, {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                inputType: INPUT_TYPE.MULTI_SELECT,
                title: formatMessage(statisticsMessages.workingPlace),
                options: workingPlacesData?.results.map((workingPlace) => ({
                    value: workingPlace.id,
                    label: workingPlace.name,
                })),
            },
            id: 'workingplace_id',
        }),
        columnHelper.accessor(PAYMENT_EXPORT_PROPS.EVENT_NAME, {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(statisticsMessages.eventType),
            },
        }),
    ];

    const formattedPaymentsByType = React.useMemo(() => {
        if (!paymentsByType) return [];
        return paymentsByType.results
            .filter((payment) => payment.result_type !== TOTAL_SPACES_NAME)
            .map((payment) => {
                const details = payment.details[0];
                const message = PAYMENT_TYPE_MAP[details.name as PAYMENT_TYPE];

                const paymentTypeName = message ? formatMessage(message) : details.name;

                return { name: paymentTypeName, amount: details.price_total };
            });
    }, [paymentsByType]);

    const formattedPaymentsByItem = React.useMemo(() => {
        if (!paymentsByItem) return [];
        return paymentsByItem.results
            .filter((payment) => payment.result_type !== TOTAL_SPACES_NAME)
            .map((payment) => {
                const details = payment.details[0];
                return {
                    name: details.name,
                    amount: details.price_total,
                };
            });
    }, [paymentsByItem]);

    const cardData = React.useMemo(() => {
        const details = totalPayments?.results[0].details[0];
        const formattedTotalPayments = details
            ? formatNumber(details?.price_total / 100, {
                  style: 'currency',
                  currency: 'BGN',
              })
            : null;
        const formattedTotalPaymentsNoVat = details
            ? formatNumber(details?.price_no_vat / 100, {
                  style: 'currency',
                  currency: 'BGN',
              })
            : null;
        return [
            {
                title: formatMessage(statisticsMessages.totalPaymentsValue),
                value: formattedTotalPayments,
            },
            {
                title: formatMessage(statisticsMessages.totalPaymentsValueWithoutVat),
                value: formattedTotalPaymentsNoVat,
            },
        ];
    }, [totalPayments]);

    const getRowData = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelection.rowSelectionState)[0];
        if (!paymentDetails || !firstSelected) return;
        return paymentDetails.results.find((payment) => payment.id.toString() === firstSelected);
    }, [paymentDetails, rowSelection.rowSelectionState]);

    return (
        <SectionBlock>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link to="/statistics/reports">
                    <FontAwesomeIcon icon={faChevronLeft} className="opacity-50" />
                    {formatMessage(statisticsMessages.backToAllReports)}
                </Link>
            </Button>
            <SectionBlockHeader>
                <SectionBlockHead>
                    <SectionBlockTitle>{formatMessage(statisticsMessages.allPayments)}</SectionBlockTitle>
                    <SectionBlockSubtitle>{formatMessage(statisticsMessages.allPaymentsSubtitle)}</SectionBlockSubtitle>
                </SectionBlockHead>
            </SectionBlockHeader>
            <SectionBlockSeparator />
            <StatisticsCards cardData={cardData}>
                <GeneralBlock className="flex-1 pb-0">
                    <GeneralBlockHeader variant="dashboard">
                        <GeneralBlockTitle>{formatMessage(statisticsMessages.paymentsByType)}</GeneralBlockTitle>
                    </GeneralBlockHeader>
                    <ResponsiveContainer width="100%" height="100%">
                        {formattedPaymentsByType.length ? (
                            <BarChart width={500} height={300} data={formattedPaymentsByType}>
                                <CartesianGrid vertical={false} strokeOpacity={0.5} />
                                <XAxis dataKey="name" xAxisId={0} />
                                <Tooltip content={<StatisticsOverviewTooltip />} />
                                <Bar
                                    dataKey="amount"
                                    fill="#026AA2"
                                    radius={[6, 6, 0, 0]}
                                    name={formatMessage(statisticsMessages.totalAmount)}
                                />
                            </BarChart>
                        ) : isLoadingByType ? (
                            <Skeleton className="mb-4 h-28" />
                        ) : (
                            <GeneralMessageBlock
                                className="mb-4 py-0"
                                title={formatMessage(tableMessages.noResults)}
                                subtitle={formatMessage(tableMessages.noResultsSubtitleFilters)}
                            />
                        )}
                    </ResponsiveContainer>
                </GeneralBlock>
                <GeneralBlock className="flex-1 pb-0">
                    <GeneralBlockHeader variant="dashboard">
                        <GeneralBlockTitle>{formatMessage(statisticsMessages.paymentsByCategory)}</GeneralBlockTitle>
                    </GeneralBlockHeader>
                    <ResponsiveContainer width="100%" height="100%">
                        {formattedPaymentsByItem.length ? (
                            <BarChart width={500} height={300} data={formattedPaymentsByItem}>
                                <CartesianGrid vertical={false} strokeOpacity={0.5} />
                                <XAxis dataKey="name" hide xAxisId={0} />
                                <Tooltip content={<StatisticsOverviewTooltip />} />
                                <Bar
                                    dataKey="amount"
                                    fill="#026AA2"
                                    radius={[6, 6, 0, 0]}
                                    name={formatMessage(statisticsMessages.totalAmount)}
                                />
                            </BarChart>
                        ) : isLoadingByItem ? (
                            <Skeleton className="mb-4 h-28" />
                        ) : (
                            <GeneralMessageBlock
                                className="mb-4 py-0"
                                title={formatMessage(tableMessages.noResults)}
                                subtitle={formatMessage(tableMessages.noResultsSubtitleFilters)}
                            />
                        )}
                    </ResponsiveContainer>
                </GeneralBlock>
            </StatisticsCards>
            <DataTable
                columns={columns}
                data={paymentDetails}
                pagination={pagination}
                sorting={sorting}
                filters={filters}
                search={search}
                semantics={{
                    title: formatMessage(statisticsMessages.payments),
                    entryName: formatMessage(statisticsMessages.payment),
                    entryNames: formatMessage(statisticsMessages.payments),
                    placeholder: formatMessage(cardMessages.cardNumber),
                    sidePanelName: PAYMENTS_PANEL_NAME,
                }}
                isError={isError}
                isLoading={isLoading}
                rowSelection={rowSelection}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
            />
            <SidePanel onClose={clearRowSelection} name={PAYMENTS_PANEL_NAME}>
                <PaymentDetails paymentDetails={getRowData} />
            </SidePanel>
        </SectionBlock>
    );
}
